'use client';

import classNames from 'classnames';
import Image from 'next/image';
import Link from 'next/link';
import { FC } from 'react';

import {
  EventItem,
  getEventSeatAvailability,
  isPastEventEnd,
} from '@/entities/event';
import {
  declension,
  formatEventDateRu,
  formatEventTimeRu,
  formatPrice,
} from '@/shared/lib';
import { Icon, Sticker } from '@/shared/ui';
import { MODAL_TYPES } from '@/widgets/modals/config/constants';
import { useModalStore } from '@/widgets/modals/model/store';

import styles from './eventCard.module.scss';

interface EventCardProps {
  event: EventItem;
  isArchive?: boolean;
}

const EventCard: FC<EventCardProps> = ({ event, isArchive = false }) => {
  const { openModal } = useModalStore();
  const registrationClosed = isPastEventEnd(event.end_at) || isArchive;
  const { noSeatsLeft, totalSeats, shouldShowLimitedSeatsSticker } =
    getEventSeatAvailability(event);

  const handleCtaClick = () => {
    if (registrationClosed) {
      return;
    }

    const payload = {
      event: {
        slug: event.slug,
        title: event.title,
        startAt: event.start_at,
        location: '-',
        price: event.price,
      },
    };

    if (noSeatsLeft) {
      openModal(MODAL_TYPES.eventWaitlist, payload);
      return;
    }

    openModal(MODAL_TYPES.eventRegistration, payload);
  };

  return (
    <article
      className={classNames(
        styles.card,
        registrationClosed && styles.card_disabled,
      )}
    >
      <Link className={styles.card__image_block} href={`/events/${event.slug}`}>
        {event.image ? (
          <Image
            fill
            alt={event.title}
            className={styles.card__image}
            sizes="(max-width: 1024px) 100vw, 50vw"
            src={event.image}
          />
        ) : (
          <div
            aria-label={event.title}
            className={styles.card__image_placeholder}
            role="img"
          />
        )}
        {registrationClosed ? (
          <Sticker className={styles.card__sticker} variant="red">
            Запись закрыта
          </Sticker>
        ) : (
          shouldShowLimitedSeatsSticker && (
            <Sticker className={styles.card__sticker} variant="white">
              Осталось {event.available_seats}{' '}
              {declension(event.available_seats, ['место', 'места', 'мест'])}!
            </Sticker>
          )
        )}
      </Link>

      <div className={styles.card__meta}>
        <span>{formatEventDateRu(event.start_at)}</span>
        <span>{formatEventTimeRu(event.start_at)}</span>
      </div>

      <Link
        className={classNames('link', styles.card__title)}
        href={`/events/${event.slug}`}
      >
        {event.title}
      </Link>

      <Link
        className={classNames(
          styles.card__descriptionBlock,
          styles.card__description,
        )}
        href={`/events/${event.slug}`}
      >
        {event.description}
      </Link>

      {!registrationClosed && (
        <p className={styles.card__price}>{formatPrice(event.price)}</p>
      )}

      <div className={styles.card__bottom}>
        {registrationClosed ? (
          <p className={styles.card__registrationClosed}>Запись закрыта</p>
        ) : (
          <>
            <button
              className={classNames('link', styles.card__cta)}
              type="button"
              onClick={handleCtaClick}
            >
              <Icon name="plusBig" size={20} />
              {noSeatsLeft ? 'В лист ожидания' : 'Записаться'}
            </button>

            {noSeatsLeft ? (
              <p className={styles.card__seats_empty}>Нет доступных мест</p>
            ) : (
              <p className={styles.card__seats}>
                {event.available_seats} из {totalSeats ?? '0'} мест доступно
              </p>
            )}
          </>
        )}
      </div>
    </article>
  );
};

export default EventCard;
